import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { fetchBusinessUnit, deleteBusinessUnit } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

/** ISO date string → "YYYY/MM/DD" for display */
const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BusinessUnitDetailPage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchBusinessUnit(businessUnitId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteBusinessUnit(businessUnitId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/business-units`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("businessUnit.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("businessDomain.title")} value={data.businessDomainName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("businessUnit.name")} value={data.businessUnitName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("businessUnit.vision")} value={data.vision} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("businessUnit.startDate")} value={formatDate(data.startDate)} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("businessUnit.endDate")} value={formatDate(data.endDate)} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("businessUnit.url")} value={data.url} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<ApartmentIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments`)}>
          {t("department.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AssignmentIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans`)}>
          {t("actionPlan.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<FolderSpecialIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects`)}>
          {t("project.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<ViewModuleIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/programs`)}>
          {t("program.list.title")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
